import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../../../context/AuthContext";
import { logActivity } from "../activityLog";
import { toast } from "react-toastify";
import { fnUrl } from "../../../utils/fnUrl";
import Editor from "react-simple-code-editor";
// @ts-ignore
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-json";
import "prismjs/themes/prism.css";

const INPUT_TYPES = ["text", "textarea", "number", "color", "json", "image", "content"] as const;

interface FieldDef {
  key: string;
  label: string;
  input: string;
  localized?: boolean;
  editable?: boolean;
}

interface BlockDef {
  type: string;
  label: string;
  template: any;
  fields: FieldDef[];
}

/** Default template value for a given input type */
function defaultValueForInput(input: string): any {
  switch (input) {
    case "content": return [];
    case "number": return 0;
    case "text": case "textarea": case "image": return "";
    case "color": case "json": default: return null;
  }
}

/** Auto-detect input type from a JSON value */
function detectInputType(value: any): string {
  if (Array.isArray(value)) return "content";
  if (typeof value === "number") return "number";
  if (typeof value === "string") return "text";
  if (value === null) return "json";
  if (typeof value === "object") return "json";
  return "text";
}

/** Generate a human label from a key: background_color → Background Color */
function keyToLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Sync fields from template keys, preserving existing customizations */
function syncFieldsFromTemplate(template: any, existingFields: FieldDef[]): FieldDef[] {
  if (typeof template !== "object" || template === null) return existingFields;

  const templateKeys = Object.keys(template).filter((k) => k !== "type");
  const existingByKey = new Map(existingFields.map((f) => [f.key, f]));

  return templateKeys.map((key) => {
    const existing = existingByKey.get(key);
    if (existing) return existing;
    return {
      key,
      label: keyToLabel(key),
      input: detectInputType(template[key]),
      localized: false,
      editable: true,
    };
  });
}

export const DEFAULT_BLOCKS: BlockDef[] = [
  {
    type: "group",
    label: "Group",
    template: { type: "group", content: [], background_color: null, insets: [24, 12, 24, 12] },
    fields: [
      { key: "content", label: "Content", input: "content", localized: false, editable: true },
      { key: "background_color", label: "Background Color", input: "color", localized: false, editable: true },
      { key: "insets", label: "Insets", input: "json", localized: false, editable: false },
    ],
  },
  {
    type: "title",
    label: "Title",
    template: { type: "title", text: "" },
    fields: [
      { key: "text", label: "Text", input: "text", localized: true, editable: true },
    ],
  },
  {
    type: "image",
    label: "Image",
    template: { type: "image", url: "", thumb_url: "" },
    fields: [
      { key: "url", label: "Image", input: "image", localized: false, editable: true },
      { key: "thumb_url", label: "Thumbnail", input: "image", localized: false, editable: false },
    ],
  },
  {
    type: "text",
    label: "Text",
    template: { type: "text", text: "" },
    fields: [
      { key: "text", label: "Text", input: "textarea", localized: true, editable: true },
    ],
  },
  {
    type: "space",
    label: "Space",
    template: { type: "space", size: 12 },
    fields: [
      { key: "size", label: "Size", input: "number", localized: false, editable: true },
    ],
  },
  {
    type: "line",
    label: "Line",
    template: { type: "line", thickness: null, color: null, indent: null, end_indent: null },
    fields: [
      { key: "thickness", label: "Thickness", input: "number", localized: false, editable: true },
      { key: "color", label: "Color", input: "color", localized: false, editable: true },
      { key: "indent", label: "Indent", input: "number", localized: false, editable: false },
      { key: "end_indent", label: "End Indent", input: "number", localized: false, editable: false },
    ],
  },
  {
    type: "row",
    label: "Row",
    template: { type: "row", content: [], weights: null },
    fields: [
      { key: "content", label: "Content", input: "content", localized: false, editable: true },
      { key: "weights", label: "Weights", input: "json", localized: false, editable: true },
    ],
  },
  {
    type: "button",
    label: "Button",
    template: { type: "button", text: "", height: 36, style: { font_size: 14, font_style: null, font_weight: "medium", color: null }, container_color: null, action: {}, insets: [0, 18, 0, 18] },
    fields: [
      { key: "text", label: "Text", input: "text", localized: true, editable: true },
      { key: "height", label: "Height", input: "number", localized: false, editable: true },
      { key: "style", label: "Style", input: "json", localized: false, editable: false },
      { key: "container_color", label: "Container Color", input: "color", localized: false, editable: true },
      { key: "action", label: "Action", input: "json", localized: false, editable: true },
      { key: "insets", label: "Insets", input: "json", localized: false, editable: false },
    ],
  },
  {
    type: "unit_selector",
    label: "Unit Selector",
    template: { type: "unit_selector", metric: [], imperial: [] },
    fields: [
      { key: "metric", label: "Metric", input: "content", localized: false, editable: true },
      { key: "imperial", label: "Imperial", input: "content", localized: false, editable: true },
    ],
  },
  {
    type: "day",
    label: "Day",
    template: { type: "day", title: "", meals: [] },
    fields: [
      { key: "title", label: "Title", input: "text", localized: true, editable: true },
      { key: "meals", label: "Meals", input: "json", localized: false, editable: true },
    ],
  },
  {
    type: "shopping_list",
    label: "Shopping List",
    template: { type: "shopping_list", name: "", color: "#ECCADA", action: { type: "shopping_list", shopping_list: { id: "", name: "", items: [] } } },
    fields: [
      { key: "name", label: "Name", input: "text", localized: true, editable: true },
      { key: "color", label: "Color", input: "color", localized: false, editable: true },
      { key: "action", label: "Action", input: "json", localized: false, editable: true },
    ],
  },
  {
    type: "decorator",
    label: "Decorator",
    template: { type: "decorator", content: [], color: null, insets: null, corner: 0, margins: null },
    fields: [
      { key: "content", label: "Content", input: "content", localized: false, editable: true },
      { key: "color", label: "Color", input: "color", localized: false, editable: true },
      { key: "insets", label: "Insets", input: "json", localized: false, editable: false },
      { key: "corner", label: "Corner", input: "number", localized: false, editable: true },
      { key: "margins", label: "Margins", input: "json", localized: false, editable: false },
    ],
  },
];

type DeployEnvFields = Array<{ key: string; value: string }>;
interface DeployState { dev: DeployEnvFields; prod: DeployEnvFields; }

type RemoteConfigEnv = "dev" | "prod";
type RemoteConfigFieldType = "boolean" | "string";
type RemoteConfigValue = boolean | string;

interface RemoteConfigFieldDef {
  key: string;
  label: string;
  type: RemoteConfigFieldType;
  description: string;
  defaultValue: RemoteConfigValue;
}

interface RemoteConfigEnvState {
  status: "idle" | "loading" | "loaded" | "publishing" | "error";
  values: Record<string, RemoteConfigValue>;
  loadedValues: Record<string, RemoteConfigValue>;
  existsInRemoteConfig: Record<string, boolean>;
  firebaseProject: string;
  error: string;
  loadedAt: string;
  publishedAt: string;
}

const REMOTE_CONFIG_FIELDS: RemoteConfigFieldDef[] = [
  {
    key: "feature_manage_subscription",
    label: "Manage Subscription",
    type: "boolean",
    description: "Shows subscription management entry in settings.",
    defaultValue: false,
  },
  {
    key: "feature_enable_log_food_scanner",
    label: "Enable Log Food Scanner",
    type: "boolean",
    description: "Enables food scanner inside the log food flow.",
    defaultValue: true,
  },
  {
    key: "paywall",
    label: "Paywall",
    type: "string",
    description: "Paywall identifier used by the mobile app.",
    defaultValue: "v101_noskip",
  },
  {
    key: "feature_enable_personal_meal_plan",
    label: "Enable Personal Meal Plan",
    type: "boolean",
    description: "Enables personal meal plan feature in the app.",
    defaultValue: false,
  },
];

const createRemoteConfigValues = (source?: Record<string, RemoteConfigValue>): Record<string, RemoteConfigValue> =>
  Object.fromEntries(
    REMOTE_CONFIG_FIELDS.map((field) => [field.key, source?.[field.key] ?? field.defaultValue])
  );

const createRemoteConfigPresence = (entries?: Array<{ key: string; existsInRemoteConfig?: boolean }>): Record<string, boolean> => {
  const byKey = new Map((entries || []).map((entry) => [entry.key, !!entry.existsInRemoteConfig]));
  return Object.fromEntries(REMOTE_CONFIG_FIELDS.map((field) => [field.key, byKey.get(field.key) || false]));
};

const createRemoteConfigEnvState = (): RemoteConfigEnvState => ({
  status: "idle",
  values: createRemoteConfigValues(),
  loadedValues: createRemoteConfigValues(),
  existsInRemoteConfig: createRemoteConfigPresence(),
  firebaseProject: "",
  error: "",
  loadedAt: "",
  publishedAt: "",
});

const getRemoteConfigChangedKeys = (envState: RemoteConfigEnvState): string[] =>
  REMOTE_CONFIG_FIELDS.filter((field) => envState.values[field.key] !== envState.loadedValues[field.key]).map((field) => field.key);

const getRemoteConfigStatusLabel = (envState: RemoteConfigEnvState): string => {
  if (envState.status === "loading") return "Loading";
  if (envState.status === "publishing") return "Publishing";
  if (envState.status === "error") return "Error";
  if (!envState.loadedAt) return "Not loaded";
  if (getRemoteConfigChangedKeys(envState).length > 0) return "Modified";
  return "Synced";
};

const formatRemoteConfigDate = (value: string): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const toDeployFields = (obj: any): DeployEnvFields =>
  Object.entries(obj || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => ({ key, value: String(value) }));

const fromDeployFields = (fields: DeployEnvFields): Record<string, string> =>
  Object.fromEntries(fields.map((f) => [f.key, f.value]));

const GeneralSettings: React.FC = () => {
  const { project } = useParams<{ project: string }>();
  const { user, firebaseUser } = useAuth();
  const [blocks, setBlocks] = useState<BlockDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [rawTemplates, setRawTemplates] = useState<Record<number, string>>({});
  const [deploy, setDeploy] = useState<DeployState>({ dev: [], prod: [] });
  const [remoteConfig, setRemoteConfig] = useState<Record<RemoteConfigEnv, RemoteConfigEnvState>>({
    dev: createRemoteConfigEnvState(),
    prod: createRemoteConfigEnvState(),
  });
  const [pushConfig, setPushConfig] = useState({ enabled: false, scheduleTime: "" });
  const [pushConfigLoaded, setPushConfigLoaded] = useState(false);
  const [savingPush, setSavingPush] = useState(false);

  useEffect(() => {
    if (!project) return;
    setLoading(true);
    Promise.all([
      getDoc(doc(db, "content", project, "_config", "blocks")),
      getDoc(doc(db, "content", project, "_config", "deploy")),
      getDoc(doc(db, "content", project, "_config", "push")),
    ])
      .then(([blocksSnap, deploySnap, pushSnap]) => {
        setBlocks(blocksSnap.exists() ? blocksSnap.data().blocks || [] : []);
        if (deploySnap.exists()) {
          const d = deploySnap.data();
          setDeploy({ dev: toDeployFields(d.dev), prod: toDeployFields(d.prod) });
        }
        if (pushSnap.exists()) {
          const p = pushSnap.data();
          setPushConfig({
            enabled: p.autoPush?.enabled ?? false,
            scheduleTime: p.autoPush?.scheduleTime ?? "",
          });
        }
        setPushConfigLoaded(true);
      })
      .catch((err) => console.error("[GeneralSettings] Load error:", err))
      .finally(() => setLoading(false));
  }, [project]);

  const save = async () => {
    if (!project) return;
    setSaving(true);
    try {
      await Promise.all([
        setDoc(doc(db, "content", project, "_config", "blocks"), {
          blocks: JSON.parse(JSON.stringify(blocks)),
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "unknown",
        }),
        setDoc(doc(db, "content", project, "_config", "deploy"), {
          dev: fromDeployFields(deploy.dev),
          prod: fromDeployFields(deploy.prod),
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "unknown",
        }),
      ]);
      
      toast.success("Saved");
      logActivity(project, {
        action: "edit",
        contentType: "general",
        docTitle: "General Settings",
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
    } catch (err) {
      console.error("[GeneralSettings] Save error:", err);
      
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const updateDeployField = (env: "dev" | "prod", index: number, field: "key" | "value", val: string) => {
    setDeploy((prev) => {
      const next = { ...prev, [env]: [...prev[env]] };
      next[env][index] = { ...next[env][index], [field]: val };
      return next;
    });
    
  };

  const addDeployField = (env: "dev" | "prod") => {
    setDeploy((prev) => ({ ...prev, [env]: [...prev[env], { key: "", value: "" }] }));
    
  };

  const removeDeployField = (env: "dev" | "prod", index: number) => {
    setDeploy((prev) => ({ ...prev, [env]: prev[env].filter((_, i) => i !== index) }));
    
  };

  const loadRemoteConfig = async (env: RemoteConfigEnv) => {
    if (!project || !firebaseUser) {
      toast.error("You need to sign in again");
      return;
    }

    setRemoteConfig((prev) => ({
      ...prev,
      [env]: { ...prev[env], status: "loading", error: "" },
    }));

    try {
      const idToken = await firebaseUser.getIdToken();
      const resp = await fetch(fnUrl("cmsRemoteConfig"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({ action: "load", project, env }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);

      const nextValues = createRemoteConfigValues(data.values || {});
      const loadedAt = new Date().toISOString();
      setRemoteConfig((prev) => ({
        ...prev,
        [env]: {
          ...prev[env],
          status: "loaded",
          values: nextValues,
          loadedValues: nextValues,
          existsInRemoteConfig: createRemoteConfigPresence(data.entries),
          firebaseProject: data.firebaseProject || "",
          error: "",
          loadedAt,
        },
      }));
      toast.success(`${env === "prod" ? "Production" : "Stage"} Remote Config loaded`);
    } catch (err: any) {
      console.error("[GeneralSettings] Remote Config load error:", err);
      setRemoteConfig((prev) => ({
        ...prev,
        [env]: { ...prev[env], status: "error", error: err.message || "Load failed" },
      }));
      toast.error(`Remote Config load failed: ${err.message}`);
    }
  };

  const updateRemoteConfigValue = (env: RemoteConfigEnv, key: string, value: RemoteConfigValue) => {
    setRemoteConfig((prev) => ({
      ...prev,
      [env]: {
        ...prev[env],
        status: prev[env].status === "error" && !prev[env].loadedAt ? "error" : "loaded",
        values: { ...prev[env].values, [key]: value },
        error: "",
      },
    }));
  };

  const publishRemoteConfig = async (env: RemoteConfigEnv) => {
    if (!project || !firebaseUser) {
      toast.error("You need to sign in again");
      return;
    }

    const envState = remoteConfig[env];
    const changedKeys = getRemoteConfigChangedKeys(envState);
    if (!envState.loadedAt) {
      toast.info("Load Remote Config first");
      return;
    }
    if (changedKeys.length === 0) {
      toast.info("No Remote Config changes to publish");
      return;
    }

    const envLabel = env === "prod" ? "Production" : "Stage";
    const confirmed = window.confirm(`Publish Remote Config to ${envLabel}?\n\n${changedKeys.join("\n")}`);
    if (!confirmed) return;

    setRemoteConfig((prev) => ({
      ...prev,
      [env]: { ...prev[env], status: "publishing", error: "" },
    }));

    try {
      const idToken = await firebaseUser.getIdToken();
      const resp = await fetch(fnUrl("cmsRemoteConfig"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          action: "publish",
          project,
          env,
          values: envState.values,
          changedKeys,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);

      const nextValues = createRemoteConfigValues(data.values || {});
      setRemoteConfig((prev) => ({
        ...prev,
        [env]: {
          ...prev[env],
          status: "loaded",
          values: nextValues,
          loadedValues: nextValues,
          existsInRemoteConfig: Object.fromEntries(REMOTE_CONFIG_FIELDS.map((field) => [field.key, true])),
          firebaseProject: data.firebaseProject || prev[env].firebaseProject,
          error: "",
          loadedAt: prev[env].loadedAt || new Date().toISOString(),
          publishedAt: data.publishedAt || new Date().toISOString(),
        },
      }));

      toast.success(`Remote Config published to ${envLabel}`);
      logActivity(project, {
        action: "edit",
        contentType: "remote_config",
        docTitle: "Remote Config",
        detail: `${envLabel}: ${changedKeys.join(", ")}`,
        user: user?.email || firebaseUser?.email || "unknown",
        photoURL: user?.photoURL || firebaseUser?.photoURL || undefined,
      });
    } catch (err: any) {
      console.error("[GeneralSettings] Remote Config publish error:", err);
      setRemoteConfig((prev) => ({
        ...prev,
        [env]: { ...prev[env], status: "error", error: err.message || "Publish failed" },
      }));
      toast.error(`Remote Config publish failed: ${err.message}`);
    }
  };

  const savePushConfig = async () => {
    if (!project) return;
    setSavingPush(true);
    try {
      await setDoc(doc(db, "content", project, "_config", "push"), {
        autoPush: {
          enabled: pushConfig.enabled,
          scheduleTime: pushConfig.scheduleTime || null,
        },
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || "unknown",
      });
      toast.success("Push config saved");
    } catch (err) {
      console.error("[GeneralSettings] Push config save error:", err);
      toast.error("Push config save failed");
    }
    setSavingPush(false);
  };

  const addBlock = () => {
    setBlocks([...blocks, { type: "", label: "", template: {}, fields: [] }]);
    setExpandedIndex(blocks.length);
    
  };

  const addFromTemplate = (templateIndex: number) => {
    const src = DEFAULT_BLOCKS[templateIndex];
    const copy: BlockDef = {
      type: src.type,
      label: src.label,
      template: JSON.parse(JSON.stringify(src.template)),
      fields: src.fields.map((f) => ({ ...f })),
    };
    setBlocks((prev) => [...prev, copy]);
    setExpandedIndex(blocks.length);
    
  };


  const updateBlock = (index: number, updated: BlockDef) => {
    setBlocks((prev) => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
    
  };

  const handleTemplateChange = (index: number, code: string) => {
    setRawTemplates((prev) => ({ ...prev, [index]: code }));
    try {
      const parsed = JSON.parse(code);
      setBlocks((prev) => {
        const next = [...prev];
        const block = { ...next[index] };
        block.template = parsed;
        block.fields = syncFieldsFromTemplate(parsed, block.fields);
        next[index] = block;
        return next;
      });
      
    } catch {
      // Invalid JSON while typing — fields update when JSON becomes valid
    }
  };

  const getTemplateCode = (index: number): string => {
    if (rawTemplates[index] !== undefined) return rawTemplates[index];
    const tpl = blocks[index].template;
    return typeof tpl === "string" ? tpl : JSON.stringify(tpl, null, 2);
  };

  const formatTemplate = (index: number) => {
    const block = blocks[index];
    if (typeof block.template === "object") {
      const formatted = JSON.stringify(block.template, null, 2);
      setRawTemplates((prev) => ({ ...prev, [index]: formatted }));
    }
  };

  const deleteBlock = (index: number) => {
    if (!window.confirm(`Delete block "${blocks[index].label || blocks[index].type}"?`)) return;
    const next = [...blocks];
    next.splice(index, 1);
    setBlocks(next);
    setRawTemplates((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
    if (expandedIndex === index) setExpandedIndex(null);
    else if (expandedIndex !== null && expandedIndex > index) setExpandedIndex(expandedIndex - 1);
    
  };

  const updateFieldDef = (blockIndex: number, fieldIndex: number, updated: FieldDef) => {
    const block = { ...blocks[blockIndex] };
    block.fields = [...block.fields];
    block.fields[fieldIndex] = updated;
    updateBlock(blockIndex, block);
  };

  if (loading) return <div className="editor__loading">Loading...</div>;

  return (
    <div className="general">
      <div className="general__header">
        <h2 className="general__title">General Settings</h2>
        <div className="general__actions">
          <button className="editor__save-btn" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="general__deploy">
        <h3 className="general__section-title">Deploy Config</h3>
        {(["dev", "prod"] as const).map((env) => (
          <div key={env} className="general__deploy-env">
            <div className="general__deploy-env-label">{env === "dev" ? "Dev / Stage" : "Production"}</div>
            {deploy[env].map((row, i) => (
              <div key={i} className="general__deploy-row">
                <input
                  className="block-input block-input--small"
                  value={row.key}
                  onChange={(e) => updateDeployField(env, i, "key", e.target.value)}
                  placeholder="key"
                />
                <input
                  className="block-input"
                  value={row.value}
                  onChange={(e) => updateDeployField(env, i, "value", e.target.value)}
                  placeholder="value"
                />
                <button className="general__remove-field" onClick={() => removeDeployField(env, i)} title="Remove">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}
            <button className="general__add-deploy-field" onClick={() => addDeployField(env)}>+ Add field</button>
          </div>
        ))}
      </div>

      {pushConfigLoaded && (
        <div className="general__push">
          <h3 className="general__section-title">Push Notifications</h3>
          <div className="general__push-body">
            <label className="general__push-toggle">
              <input
                type="checkbox"
                checked={pushConfig.enabled}
                onChange={(e) => setPushConfig((p) => ({ ...p, enabled: e.target.checked }))}
              />
              <span>Auto-push after deploy</span>
            </label>
            <div className="general__push-time">
              <label className="general__push-time-label">Default schedule time</label>
              <input
                type="time"
                className="block-input"
                value={pushConfig.scheduleTime}
                onChange={(e) => setPushConfig((p) => ({ ...p, scheduleTime: e.target.value }))}
                placeholder="Leave empty for immediate"
              />
            </div>
            <button className="editor__save-btn" onClick={savePushConfig} disabled={savingPush}>
              {savingPush ? "Saving..." : "Save Push Config"}
            </button>
          </div>
        </div>
      )}

      <div className="general__remote">
        <div className="general__remote-header">
          <div>
            <h3 className="general__section-title">Remote Config</h3>
            <p className="general__remote-hint">Editable keys are synced with the mobile feature config schema.</p>
          </div>
        </div>

        <div className="general__remote-grid">
          {(["dev", "prod"] as const).map((env) => {
            const envState = remoteConfig[env];
            const changedKeys = getRemoteConfigChangedKeys(envState);
            const envLabel = env === "prod" ? "Production" : "Stage";
            const isLoaded = !!envState.loadedAt;
            const isBusy = envState.status === "loading" || envState.status === "publishing";

            return (
              <section key={env} className="general__remote-card">
                <div className="general__remote-card-header">
                  <div>
                    <div className="general__remote-card-title">{envLabel}</div>
                    <div className={`general__remote-status general__remote-status--${getRemoteConfigStatusLabel(envState).toLowerCase().replace(/\s+/g, "-")}`}>
                      {getRemoteConfigStatusLabel(envState)}
                    </div>
                  </div>
                  <div className="general__remote-actions">
                    <button className="general__remote-btn" onClick={() => loadRemoteConfig(env)} disabled={isBusy}>
                      {envState.status === "loading" ? "Loading..." : "Load"}
                    </button>
                    <button
                      className="editor__save-btn"
                      onClick={() => publishRemoteConfig(env)}
                      disabled={!isLoaded || changedKeys.length === 0 || isBusy}
                    >
                      {envState.status === "publishing" ? "Publishing..." : "Save & Publish"}
                    </button>
                  </div>
                </div>

                <div className="general__remote-meta">
                  <span>{envState.firebaseProject ? `Firebase project: ${envState.firebaseProject}` : "Firebase project will be resolved from Deploy Config"}</span>
                  {envState.loadedAt && <span>Loaded: {formatRemoteConfigDate(envState.loadedAt)}</span>}
                  {envState.publishedAt && <span>Published: {formatRemoteConfigDate(envState.publishedAt)}</span>}
                </div>

                {envState.error && <div className="general__remote-error">{envState.error}</div>}

                <div className="general__remote-list">
                  {REMOTE_CONFIG_FIELDS.map((field) => {
                    const changed = envState.values[field.key] !== envState.loadedValues[field.key];
                    const missing = isLoaded && !envState.existsInRemoteConfig[field.key];

                    return (
                      <div key={field.key} className={`general__remote-row${changed ? " general__remote-row--changed" : ""}`}>
                        <div className="general__remote-field-main">
                          <div className="general__remote-field-head">
                            <span className="general__remote-field-label">{field.label}</span>
                            <code className="general__remote-field-key">{field.key}</code>
                          </div>
                          <div className="general__remote-field-description">{field.description}</div>
                          <div className="general__remote-field-notes">
                            <span>App default: {String(field.defaultValue)}</span>
                            {missing ? <span>Using app default</span> : <span>Present in Remote Config</span>}
                            {changed && <span>Modified</span>}
                          </div>
                        </div>

                        <div className="general__remote-field-control">
                          {field.type === "boolean" ? (
                            <label className="general__remote-toggle">
                              <input
                                type="checkbox"
                                checked={Boolean(envState.values[field.key])}
                                disabled={!isLoaded || isBusy}
                                onChange={(e) => updateRemoteConfigValue(env, field.key, e.target.checked)}
                              />
                              <span>{Boolean(envState.values[field.key]) ? "Enabled" : "Disabled"}</span>
                            </label>
                          ) : (
                            <input
                              className="block-input"
                              value={String(envState.values[field.key] ?? "")}
                              disabled={!isLoaded || isBusy}
                              onChange={(e) => updateRemoteConfigValue(env, field.key, e.target.value)}
                              placeholder={String(field.defaultValue)}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </div>

      <div className="general__body">
        <div className="general__main">
          {blocks.length === 0 && (
            <div className="general__empty">
              <p>No block schemas defined yet.</p>
              <p>Add blocks from the template library on the right, or create a custom one below.</p>
            </div>
          )}

          <div className="general__list">
            {blocks.map((block, i) => (
              <div key={i} className={`general__card${expandedIndex === i ? " general__card--expanded" : ""}`}>
                <div className="general__card-header" onClick={() => setExpandedIndex(expandedIndex === i ? null : i)}>
                  <div className="general__card-info">
                    <span className="general__card-type">{block.type || "---"}</span>
                    <span className="general__card-label">{block.label || "Untitled"}</span>
                    <span className="general__card-count">{block.fields.length} field{block.fields.length !== 1 ? "s" : ""}</span>
                  </div>
                  <svg className={`general__card-chevron${expandedIndex === i ? " general__card-chevron--open" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>

                {expandedIndex === i && (
                  <div className="general__card-body">
                    <div className="general__inline-row">
                      <div className="general__inline-field">
                        <label>Type</label>
                        <input
                          className="block-input block-input--small"
                          value={block.type}
                          onChange={(e) => updateBlock(i, { ...block, type: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })}
                          placeholder="snake_case, e.g. plain_text"
                        />
                      </div>
                      <div className="general__inline-field">
                        <label>Label</label>
                        <input
                          className="block-input block-input--small"
                          value={block.label}
                          onChange={(e) => updateBlock(i, { ...block, label: e.target.value })}
                          placeholder="Display name, e.g. Title"
                        />
                      </div>
                    </div>
                    <div className="general__field-row">
                      <label>Template (JSON)</label>
                      <div className="general__code-editor">
                        <Editor
                          value={getTemplateCode(i)}
                          onValueChange={(code) => handleTemplateChange(i, code)}
                          onBlur={() => formatTemplate(i)}
                          highlight={(code) => highlight(code, languages.json, "json")}
                          padding={12}
                          style={{
                            fontFamily: "var(--font-mono, 'SF Mono', 'Fira Code', monospace)",
                            fontSize: "0.8125rem",
                            lineHeight: 1.5,
                            minHeight: 80,
                          }}
                        />
                      </div>
                    </div>

                    <div className="general__fields-section">
                      <div className="general__fields-header">
                        <span>Fields (from template)</span>
                      </div>
                      {block.fields.length === 0 && (
                        <div className="general__fields-empty">Add keys to the template JSON above</div>
                      )}
                      {block.fields.map((field, fi) => (
                        <div key={field.key} className={`general__field-def${field.editable === false ? " general__field-def--hidden" : ""}`}>
                          <span className="general__field-key">{field.key}</span>
                          <input
                            className="block-input block-input--tiny"
                            value={field.label}
                            onChange={(e) => updateFieldDef(i, fi, { ...field, label: e.target.value })}
                            placeholder="Label"
                          />
                          <select
                            className="block-input block-input--tiny"
                            value={field.input}
                            onChange={(e) => {
                              const newInput = e.target.value;
                              const canLocalize = newInput === "text" || newInput === "textarea";
                              const updatedField = { ...field, input: newInput, localized: canLocalize ? field.localized : false };
                              // Update template value to match new input type
                              setBlocks((prev) => {
                                const next = [...prev];
                                const b = { ...next[i] };
                                const tpl = typeof b.template === "object" && b.template !== null ? { ...b.template } : {};
                                tpl[field.key] = defaultValueForInput(newInput);
                                b.template = tpl;
                                b.fields = [...b.fields];
                                b.fields[fi] = updatedField;
                                next[i] = b;
                                setRawTemplates((p) => ({ ...p, [i]: JSON.stringify(tpl, null, 2) }));
                                return next;
                              });
                              
                            }}
                          >
                            {INPUT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <label className={`general__localized-toggle${field.input !== "text" && field.input !== "textarea" ? " general__localized-toggle--disabled" : ""}`}>
                            <input
                              type="checkbox"
                              checked={!!field.localized}
                              disabled={field.input !== "text" && field.input !== "textarea"}
                              onChange={(e) => updateFieldDef(i, fi, { ...field, localized: e.target.checked })}
                            />
                            <span>Localizable</span>
                          </label>
                          <label className="general__localized-toggle">
                            <input
                              type="checkbox"
                              checked={field.editable !== false}
                              onChange={(e) => updateFieldDef(i, fi, { ...field, editable: e.target.checked })}
                            />
                            <span>Editable</span>
                          </label>
                          <button
                            className="general__remove-field"
                            onClick={() => {
                              const b = { ...blocks[i] };
                              const tpl = typeof b.template === "object" && b.template !== null ? { ...b.template } : {};
                              delete tpl[field.key];
                              b.template = tpl;
                              b.fields = b.fields.filter((_, idx) => idx !== fi);
                              updateBlock(i, b);
                              setRawTemplates((prev) => ({ ...prev, [i]: JSON.stringify(tpl, null, 2) }));
                            }}
                            title="Remove from template"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>

                    <button className="general__delete-block" onClick={() => deleteBlock(i)}>Delete Block</button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button className="general__add-btn" onClick={addBlock}>+ Add Empty Block</button>
        </div>

        <aside className="general__templates">
          <div className="general__templates-header">Templates</div>
          <div className="general__templates-list">
            {DEFAULT_BLOCKS.map((tpl, i) => (
              <button
                key={tpl.type}
                className="general__template-item"
                onClick={() => addFromTemplate(i)}
                title={`Add ${tpl.label}`}
              >
                <span className="general__template-label">{tpl.label}</span>
                <span className="general__template-fields">{tpl.fields.length}f</span>
                <span className="general__template-add">+</span>
              </button>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );

};

export default GeneralSettings;
